import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { Toaster as BildirimToaster } from '@/components/ui/toaster';
import App from './App';
import 'primereact/resources/themes/lara-light-cyan/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'quill/dist/quill.snow.css';
import './index.css';
import { CEVRIMDISI_GC_MS, onbellegiSakla, onbellegiYukle, otomatikIndirmeyiBaslat } from '@/lib/cevrimdisi/onbellek';
import { getStoredUser } from '@/lib/auth';
import { kuyruguBaslat } from '@/lib/cevrimdisi/kuyruk';
import { CevrimdisiRozeti } from '@/components/cevrimdisi/CevrimdisiRozeti';

/*
 * ÇEVRİMDIŞI SAHA DOLDURMA: sorgular gözlemcisi kalmasa da bir gün bellekte
 * durur ve cihaza yazılır (bkz. `lib/cevrimdisi`); bağlantı yokken ekranlar
 * son bilinen veriyle açılır.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { gcTime: CEVRIMDISI_GC_MS },
    // Mutasyonlar bağlantı yokken DURAKLATILMAZ: yazmalar çevrimdışı kuyruğa
    // kendisi girer (React Query'nin varsayılanı istek fonksiyonunu hiç çağırmıyordu).
    mutations: { networkMode: 'always' },
  },
});

/*
 * YENİ SÜRÜM YAYINLANINCA açık sekme eski paketle kalıyor; sayfalar tembel
 * yüklendiği için ilk girilen ekranın parçası (eski hash'li dosya) sunucuda
 * artık yok ve ekran açılmıyordu. Vite bu durumda `vite:preloadError` atar:
 * sayfa BİR KEZ yenilenip yeni index.html ve paketler alınır. Bayrak, sunucu
 * gerçekten bozuksa sonsuz yenileme döngüsüne girilmesin diye; başarılı bir
 * açılıştan sonra temizleniyor.
 */
const YENILEME_BAYRAGI = 'audex:surum-yenilendi';
window.addEventListener('vite:preloadError', (e) => {
  try {
    if (sessionStorage.getItem(YENILEME_BAYRAGI)) return;
    sessionStorage.setItem(YENILEME_BAYRAGI, '1');
  } catch {
    return;
  }
  e.preventDefault();
  window.location.reload();
});
window.setTimeout(() => {
  try {
    sessionStorage.removeItem(YENILEME_BAYRAGI);
  } catch {
    /* depolama kapalı */
  }
}, 10_000);

/*
 * UYGULAMA KABUĞU çevrimdışı açılsın diye service worker (yalnız üretimde:
 * geliştirmede Vite'ın modül sunucusuyla çakışır). Bkz. `public/sw.js`.
 */
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      /* desteklenmiyor / izin yok: çevrimdışı kabuk olmadan devam */
    });
  });
}

/*
 * KALICI DEPOLAMA isteği: tarayıcı yer daraldığında (ya da Safari uzun süre
 * açılmayan sitede) cihazdaki çevrimdışı veriyi ve bekleyen kayıtları
 * silebiliyor. İzin verilirse bu veri ancak kullanıcı silerse gider.
 */
void navigator.storage?.persist?.().catch(() => undefined);

// Önbellek diskten yüklenmeden çizilirse çevrimdışı açılışta ekranlar boş kalır.
void onbellegiYukle(queryClient).finally(() => {
  onbellegiSakla(queryClient);
  kuyruguBaslat(queryClient);
  otomatikIndirmeyiBaslat(queryClient, () => getStoredUser()?.role);
  ciz();
});

function ciz() {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
          <Toaster richColors position="top-right" />
          {/* `useToast` (hooks/use-toast) bildirimleri — sonner'dan ayrı bir kuyruk;
              bu bileşen kurulu değilken o kancayla verilen hiçbir bildirim görünmüyordu. */}
          <BildirimToaster />
          <CevrimdisiRozeti />
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>,
  );
}
